import { setLight, clearLights, getLightStyleForGid } from "./lighting.js";
import { spawnHuman } from "../entities/humanAI.js";
import { spawnGuardRobot } from "../entities/robotAI.js";
import { spawnHomelessGuy } from "../entities/homelessGuy.js";
import { isLoaded, saveCheckpoint } from "./memory.js";
import { GAME_CONFIG } from "../config/gameConfig.js";
import {
    SPRITEFUSION_SPRITE,
    buildCollisionRows,
    buildRoomRectsFromCollision,
    buildRoomRectsFromRegionLayer,
    findFloorSpawnCell,
    findNearestPassableCell,
    getLayer,
    getLayerZ,
    getMarkerCells,
    getRenderableLayers,
    getSpriteFusionMap,
    getSpritesheetImage,
    getTileGid,
    hasWalkableLayer,
    tileGidToFrame,
} from "./spritefusionMap.js";
import { setRooms, clearRooms, getRoomAt, getNearestRoom } from "./rooms.js";
import { clearScentMarks } from "./sniff.js";
import { clearAlarmLockdowns } from "./alarmLockdown.js";
import { isDebugModeVisible } from "./debugState.js";
import { clearCutsceneLocks } from "./cutsceneState.js";
import { createRamLatchPuzzle, collectRamLatchFragmentById } from "../entities/ramLatch.js";
import { queueTextbox, isTextboxOpen } from "../ui/textboxUI.js";
import { setPerfStat } from "./perf.js";

/**
 * Facility level — SpriteFusion map + lightweight path grid + room fog
 */

const TILE_W = GAME_CONFIG.level.tileWidth;
const TILE_H = GAME_CONFIG.level.tileHeight;
const CLUE_LAYER_NAMES = ["clues", "clue"];
const LIGHT_LAYER_NAMES = ["lights", "light"];
const SIGHT_BLOCK_LAYER_NAMES = ["decor"];
const DOOR_LAYER_KEYS = new Set(["door", "doors"]);
const AD_LAYER_KEYS = new Set(["ad", "ads"]);
const CLUE_LAYER_KEYS = new Set(["clue", "clues"]);
const TUNNEL_LAYER_KEYS = new Set(["tunnel", "tunnels"]);
/** Layers that change opacity / visibility at runtime — stay as GameObjs. */
const DYNAMIC_LAYER_KEYS = new Set([
    ...DOOR_LAYER_KEYS,
    ...AD_LAYER_KEYS,
    ...CLUE_LAYER_KEYS,
    ...TUNNEL_LAYER_KEYS,
]);
const DOOR_NORMAL_OPACITY = 1;
const DOOR_PLAYER_UNDER_OPACITY = 0.35;
const AD_NORMAL_OPACITY = 1;
const AD_BLOCKED_OPACITY = 0;
const CLUE_HIDDEN_OPACITY = 0;
const CLUE_VISIBLE_OPACITY = 0.95;
const TUNNEL_VISIBLE_OPACITY = 1;
const TUNNEL_HIDDEN_OPACITY = 0;
const CHECKPOINT_ACTIVATION_RADIUS = 18;
const R2_SPAWN_ALARM_ROOM_ID = 2;
const R2_SPAWN_ALARM_DISTANCE_TILES = 5;
const POSITION_ANCHOR_LAYERS = ["anchor", "anchors", "origin", "positionanchor"];
const POSITION_ANCHOR_FALLBACK_CELL = { x: 12, y: 6 };
const INTERACTABLES = [
    {
        id: "crate_fragment",
        cells: [
            { x: 32, y: 50 },
        ],
        text: "The crate is taped shut. Pry it open and read the damp logs inside.",
        triggered: false,
        onTrigger: () => {
            queueTextbox({
                tag: "SYS // CRATE_LOG",
                channel: "R5_LATCH",
                text: "The crate opens to reveal soggy logs, and a flat keycard slips free.",
                variant: "system",
            });
            collectRamLatchFragmentById("crate");
            return true;
        },
    },
    {
        id: "toilet",
        cells: [
            { x: 35, y: 45 },
        ],
        text: "The toilet is cracked and damp. Something clinks inside.",
        triggered: false,
        onTrigger: () => {
            collectRamLatchFragmentById("toilet");
            return true;
        },
    },
];
const TILE_POSITIONS = {
    dogSpawn: { dx: -3, dy: -1 },
    checkpoints: [
        { dx: 20, dy: -1 },
        { dx: 33, dy: 14 },
        { dx: 53, dy: 18 },
        { dx: 72, dy: 19 },
    ],
    humanGuard: { dx: 66, dy: 18 },
    homelessGuy: { dx: 102, dy: -32 },
    patrolHumans: [
        { dx: 2, dy: -1 },
    ],
    circlingHumans: [
        { dx: 43, dy: 15 },
    ],
    suiteHumans: [
        { dx: 97, dy: 19 },
    ],
    guardRobots: [
        { dx: 33, dy: 50 },
        { dx: 65, dy: 16 },
        { dx: 68, dy: 15 },
        { dx: 65, dy: 21 },
        { dx: 77, dy: 57 },
        { dx: 80, dy: 58 },
        { dx: 76, dy: 58 },
        { dx: 60, dy: 19 },
        { dx: 93, dy: 18 },
        { dx: 62, dy: 22 },
        { dx: 61, dy: 15 },
        { dx: -10, dy: -2 },
        { dx: 46, dy: 16 },
    ],
};

let collisionRows = [];
let sightBlockRows = [];
let currentLevel = null;
let tunnelGroupsByCell = new Map();
let adLayerTiles = [];
let clueLayerTiles = [];
let tunnelLayerTiles = [];
let doorOverlayTiles = [];
let lastAdBlockerLoaded = null;
let lastSniffLoaded = null;
let lastTunnelGroupId = undefined;
/** Retain HTML canvases until KAPLAY finishes uploading baked sprites. */
let bakedHtmlCanvases = [];

export function getLevel() {
    return currentLevel;
}

function ensureCollisionRows() {
    if (collisionRows.length > 0) return;
    collisionRows = buildCollisionRows(getSpriteFusionMap());
}

function cellToWorldCenter(cell) {
    return vec2(cell.x * TILE_W + TILE_W / 2, cell.y * TILE_H + TILE_H / 2);
}

function getPositionAnchorCell(map) {
    return getMarkerCells(map, POSITION_ANCHOR_LAYERS)[0] || POSITION_ANCHOR_FALLBACK_CELL;
}

export function getPositionAnchorDebugCell() {
    return getPositionAnchorCell(getSpriteFusionMap());
}

function worldCellByAnchorOffset(map, offset) {
    const anchorCell = getPositionAnchorCell(map);
    const targetX = anchorCell.x + offset.dx;
    const targetY = anchorCell.y + offset.dy;
    return cellToWorldCenter(findNearestPassableCell(collisionRows, targetX, targetY));
}

function r2SpawnProximityAlarmOptions(posVec) {
    const room = getRoomAt(posVec);
    if (room?.id !== R2_SPAWN_ALARM_ROOM_ID) return {};

    return {
        proximityAlarm: {
            roomId: R2_SPAWN_ALARM_ROOM_ID,
            origin: getSpawnPos(),
            distance: R2_SPAWN_ALARM_DISTANCE_TILES * TILE_W,
        },
    };
}

export function isWalkableWorldPos(worldPos) {
    if (!worldPos) return false;
    ensureCollisionRows();
    const x = Math.floor(worldPos.x / TILE_W);
    const y = Math.floor(worldPos.y / TILE_H);
    if (y < 0 || y >= collisionRows.length) return false;
    if (x < 0 || x >= collisionRows[y].length) return false;
    return collisionRows[y][x] === ".";
}

/**
 * Move `from` by `delta` without leaving walkable tiles.
 * Tries full move, then X-only / Y-only slides. Used by knockback / lunges.
 */
export function movePosOnWalkable(from, delta) {
    if (!from || !delta) {
        return { pos: from, moved: false, hitWall: false };
    }

    const full = from.add(delta);
    if (isWalkableWorldPos(full)) {
        return { pos: full, moved: true, hitWall: false };
    }

    let pos = from;
    let moved = false;
    let hitWall = true;

    if (delta.x !== 0) {
        const onlyX = vec2(from.x + delta.x, from.y);
        if (isWalkableWorldPos(onlyX)) {
            pos = onlyX;
            moved = true;
        }
    }

    if (delta.y !== 0) {
        const onlyY = vec2(pos.x, from.y + delta.y);
        if (isWalkableWorldPos(onlyY)) {
            pos = onlyY;
            moved = true;
        }
    }

    return { pos, moved, hitWall };
}

export function resolveWalkablePos(worldPos) {
    if (!worldPos) return getSpawnPos();
    ensureCollisionRows();
    const x = Math.floor(worldPos.x / TILE_W);
    const y = Math.floor(worldPos.y / TILE_H);
    return cellToWorldCenter(findNearestPassableCell(collisionRows, x, y));
}

function buildSightBlockRows(map) {
    const decor = getLayer(map, SIGHT_BLOCK_LAYER_NAMES);

    return Array.from({ length: map.height }, (_, y) => {
        let row = "";
        for (let x = 0; x < map.width; x++) {
            const blocksByCollision = collisionRows[y]?.[x] !== ".";
            const blocksByDecor = decor ? getTileGid(decor, x, y) > 0 : false;
            row += blocksByCollision || blocksByDecor ? "#" : ".";
        }
        return row;
    });
}

function isSightBlockedCell(x, y) {
    if (y < 0 || y >= sightBlockRows.length) return true;
    if (x < 0 || x >= sightBlockRows[y].length) return true;
    return sightBlockRows[y][x] === "#";
}

export function hasLineOfSightWorld(fromPos, toPos) {
    if (!fromPos || !toPos) return false;
    if (sightBlockRows.length === 0) {
        sightBlockRows = buildSightBlockRows(getSpriteFusionMap());
    }

    const from = vec2(fromPos.x, fromPos.y);
    const to = vec2(toPos.x, toPos.y);
    const delta = to.sub(from);
    const dist = delta.len();
    if (dist <= 0.1) return true;

    const steps = Math.max(1, Math.ceil(dist / 8));
    for (let i = 1; i < steps; i++) {
        const p = from.add(delta.scale(i / steps));
        const cellX = Math.floor(p.x / TILE_W);
        const cellY = Math.floor(p.y / TILE_H);
        if (isSightBlockedCell(cellX, cellY)) return false;
    }

    return true;
}

export function getSightEndPoint(fromPos, angleDeg, maxDistance) {
    if (!fromPos) return null;
    if (sightBlockRows.length === 0) {
        sightBlockRows = buildSightBlockRows(getSpriteFusionMap());
    }

    const start = vec2(fromPos.x, fromPos.y);
    const dir = Vec2.fromAngle(angleDeg);
    const dist = Math.max(0, maxDistance || 0);
    const steps = Math.max(1, Math.ceil(dist / 8));

    let lastOpen = start;
    for (let i = 1; i <= steps; i++) {
        const stepDist = Math.min(dist, i * 8);
        const p = start.add(dir.scale(stepDist));
        const cellX = Math.floor(p.x / TILE_W);
        const cellY = Math.floor(p.y / TILE_H);
        if (isSightBlockedCell(cellX, cellY)) {
            return lastOpen;
        }
        lastOpen = p;
    }

    return start.add(dir.scale(dist));
}

function worldToCell(worldPos) {
    return {
        x: Math.floor(worldPos.x / TILE_W),
        y: Math.floor(worldPos.y / TILE_H),
    };
}

function collectWalkableEdgeCells() {
    ensureCollisionRows();
    const rows = collisionRows.length;
    const cols = rows > 0 ? collisionRows[0].length : 0;
    const cells = [];

    for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
            if (collisionRows[y][x] !== ".") continue;
            const touchesEdge = x === 0 || y === 0 || x === cols - 1 || y === rows - 1;
            const nearEdge = x <= 1 || y <= 1 || x >= cols - 2 || y >= rows - 2;
            if (touchesEdge || nearEdge) {
                cells.push({ x, y });
            }
        }
    }

    return cells;
}

export function getFleeTargetAwayFrom(worldPos, threatPos) {
    const fallback = isWalkableWorldPos(worldPos) ? worldPos : getSpawnPos();
    const edgeCells = collectWalkableEdgeCells();
    if (edgeCells.length === 0) return fallback;

    const fromCell = worldToCell(worldPos);
    const threatCell = threatPos ? worldToCell(threatPos) : fromCell;
    let best = edgeCells[0];
    let bestScore = -Infinity;

    for (const cell of edgeCells) {
        const threatDx = cell.x - threatCell.x;
        const threatDy = cell.y - threatCell.y;
        const selfDx = cell.x - fromCell.x;
        const selfDy = cell.y - fromCell.y;
        const threatDist = threatDx * threatDx + threatDy * threatDy;
        const selfDist = selfDx * selfDx + selfDy * selfDy;
        const score = threatDist - selfDist * 0.18;
        if (score > bestScore) {
            bestScore = score;
            best = cell;
        }
    }

    return cellToWorldCenter(best);
}

/** Snap invalid/outdated save positions onto the nearest walkable floor cell. */
export function resolvePlayablePos(worldPos) {
    if (isWalkableWorldPos(worldPos)) {
        return vec2(worldPos.x, worldPos.y);
    }
    return getSpawnPos();
}

function addCheckpointAt(centerPos, roomId = 1) {
    const chk = add([
        circle(6),
        pos(centerPos),
        anchor("center"),
        color(0, 220, 255),
        opacity(0),
        z(5),
        "checkpoint_tile",
        {
            triggered: false,
            roomId,
        },
    ]);

    function activateCheckpoint() {
        if (chk.triggered) return;
        chk.triggered = true;
        chk.color = rgb(0, 255, 180);
        saveCheckpoint(centerPos, roomId);
    }

    chk.onUpdate(() => {
        chk.opacity = isDebugModeVisible() ? 0.6 : 0;
        if (chk.triggered) return;
        const player = get("player")[0];
        if (
            player?.exists?.()
            && isLoaded("core_locomotion")
            && isLoaded("core_vision")
            && player.pos.dist(centerPos) <= CHECKPOINT_ACTIVATION_RADIUS
        ) {
            activateCheckpoint();
        }
    });
}

export function getSpawnPos() {
    const map = getSpriteFusionMap();
    ensureCollisionRows();
    return worldCellByAnchorOffset(map, TILE_POSITIONS.dogSpawn);
}

function cellKey(x, y) {
    return `${x},${y}`;
}

function buildLayerTileGroups(layer) {
    const visited = Array.from({ length: layer.height }, () =>
        Array(layer.width).fill(false),
    );
    const groupsByCell = new Map();
    let nextGroupId = 1;

    for (let y = 0; y < layer.height; y++) {
        for (let x = 0; x < layer.width; x++) {
            if (visited[y][x] || getTileGid(layer, x, y) === 0) continue;

            const groupId = nextGroupId++;
            const stack = [{ x, y }];
            visited[y][x] = true;

            while (stack.length > 0) {
                const cell = stack.pop();
                groupsByCell.set(cellKey(cell.x, cell.y), groupId);

                for (const n of [
                    { x: cell.x + 1, y: cell.y },
                    { x: cell.x - 1, y: cell.y },
                    { x: cell.x, y: cell.y + 1 },
                    { x: cell.x, y: cell.y - 1 },
                ]) {
                    if (n.x < 0 || n.y < 0 || n.x >= layer.width || n.y >= layer.height) continue;
                    if (visited[n.y][n.x] || getTileGid(layer, n.x, n.y) === 0) continue;
                    visited[n.y][n.x] = true;
                    stack.push(n);
                }
            }
        }
    }

    return groupsByCell;
}

function getNearbyTunnelGroupId(cellX, cellY) {
    return tunnelGroupsByCell.get(cellKey(cellX, cellY)) ?? null;
}

export function isWorldPosInTunnel(worldPos) {
    if (!worldPos) return false;
    const cellX = Math.floor(worldPos.x / TILE_W);
    const cellY = Math.floor(worldPos.y / TILE_H);
    return getNearbyTunnelGroupId(cellX, cellY) != null;
}

function freeBakedMapLayers() {
    destroyAll("baked_map_layer");
    bakedHtmlCanvases = [];
}

/**
 * Bake a static layer on an HTML canvas from the raw spritesheet, then
 * register it as a normal KAPLAY sprite (reliable world rendering).
 * @returns {number} painted tile count, or -1 if sheet isn't ready (caller falls back).
 */
function bakeStaticMapLayer(map, layer) {
    const sheet = getSpritesheetImage();
    if (!sheet) return -1;

    const worldW = map.width * TILE_W;
    const worldH = map.height * TILE_H;
    const html = document.createElement("canvas");
    html.width = worldW;
    html.height = worldH;
    const ctx = html.getContext("2d");
    if (!ctx) return -1;
    ctx.imageSmoothingEnabled = false;

    const cols = map.tileset.columns;
    const srcTw = map.tileset.tileWidth;
    const srcTh = map.tileset.tileHeight;
    const firstGid = map.tileset.firstgid;
    let painted = 0;

    for (let y = 0; y < layer.height; y++) {
        for (let x = 0; x < layer.width; x++) {
            const gid = getTileGid(layer, x, y);
            if (gid === 0) continue;
            const frame = Math.max(0, gid - firstGid);
            const sx = (frame % cols) * srcTw;
            const sy = Math.floor(frame / cols) * srcTh;
            ctx.drawImage(
                sheet,
                sx,
                sy,
                srcTw,
                srcTh,
                x * TILE_W,
                y * TILE_H,
                TILE_W,
                TILE_H,
            );
            painted += 1;
        }
    }

    if (painted === 0) return 0;

    bakedHtmlCanvases.push(html);
    const spriteName = `baked_map_${layer.key}`;
    loadSprite(spriteName, html, { filter: "nearest" });
    add([
        sprite(spriteName),
        pos(0, 0),
        anchor("topleft"),
        z(getLayerZ(layer.name)),
        "baked_map_layer",
    ]);

    return painted;
}

function spawnDynamicOrFallbackTiles(map, layer, opts) {
    const {
        isDoorLayer,
        isAdLayer,
        isClueLayer,
        isTunnelLayer,
        layerGroups,
    } = opts;
    const layerZ = getLayerZ(layer.name);
    let count = 0;

    for (let y = 0; y < layer.height; y++) {
        for (let x = 0; x < layer.width; x++) {
            const gid = getTileGid(layer, x, y);
            if (gid === 0) continue;
            const tunnelGroupId = layerGroups?.get(cellKey(x, y)) ?? null;
            count += 1;

            add([
                sprite(SPRITEFUSION_SPRITE, {
                    frame: tileGidToFrame(map, gid),
                }),
                pos(x * TILE_W, y * TILE_H),
                anchor("topleft"),
                scale(TILE_W / map.tileWidth, TILE_H / map.tileHeight),
                z(layerZ),
                ...(isDoorLayer
                    ? [opacity(DOOR_NORMAL_OPACITY), "door_overlay", { tileX: x, tileY: y }]
                    : []),
                ...(isAdLayer
                    ? [opacity(AD_NORMAL_OPACITY), "ad_layer_tile"]
                    : []),
                ...(isClueLayer
                    ? [opacity(isLoaded("action_sniff") ? CLUE_VISIBLE_OPACITY : CLUE_HIDDEN_OPACITY), "clue_layer_tile"]
                    : []),
                ...(isTunnelLayer
                    ? [
                        opacity(TUNNEL_HIDDEN_OPACITY),
                        "tunnel_layer_tile",
                        { tileX: x, tileY: y, tunnelGroupId },
                    ]
                    : []),
            ]);
        }
    }

    return count;
}

function renderMapTiles(map) {
    freeBakedMapLayers();
    tunnelGroupsByCell = new Map();
    let tileEntityCount = 0;
    let bakedTileCount = 0;
    let bakedLayerCount = 0;

    getRenderableLayers(map).forEach((layer) => {
        const isDoorLayer = DOOR_LAYER_KEYS.has(layer.key);
        const isAdLayer = AD_LAYER_KEYS.has(layer.key);
        const isClueLayer = CLUE_LAYER_KEYS.has(layer.key);
        const isTunnelLayer = TUNNEL_LAYER_KEYS.has(layer.key);
        const isDynamic = DYNAMIC_LAYER_KEYS.has(layer.key);
        const layerGroups = isTunnelLayer ? buildLayerTileGroups(layer) : null;
        if (isTunnelLayer) {
            tunnelGroupsByCell = layerGroups;
        }

        const spawnOpts = {
            isDoorLayer,
            isAdLayer,
            isClueLayer,
            isTunnelLayer,
            layerGroups,
        };

        if (!isDynamic) {
            const painted = bakeStaticMapLayer(map, layer);
            if (painted > 0) {
                bakedTileCount += painted;
                bakedLayerCount += 1;
                return;
            }
            if (painted === 0) return;
            // Sheet not ready — keep the game visible with per-tile sprites.
            console.warn(`[map] bake fallback for layer "${layer.name}"`);
            tileEntityCount += spawnDynamicOrFallbackTiles(map, layer, spawnOpts);
            return;
        }

        tileEntityCount += spawnDynamicOrFallbackTiles(map, layer, spawnOpts);
    });

    setPerfStat("mapTiles", tileEntityCount);
    setPerfStat("bakedTiles", bakedTileCount);
    setPerfStat("bakedLayers", bakedLayerCount);
    setPerfStat("mapSize", `${map.width}x${map.height}`);
}

function refreshLayerTileCaches() {
    adLayerTiles = get("ad_layer_tile");
    clueLayerTiles = get("clue_layer_tile");
    tunnelLayerTiles = get("tunnel_layer_tile");
    doorOverlayTiles = get("door_overlay");
    lastAdBlockerLoaded = null;
    lastSniffLoaded = null;
    lastTunnelGroupId = undefined;
}

function updateAdLayerVisibility() {
    const adBlockerLoaded = isLoaded("utility_ad_blocker");
    if (adBlockerLoaded === lastAdBlockerLoaded) return;
    lastAdBlockerLoaded = adBlockerLoaded;

    const targetOpacity = adBlockerLoaded
        ? AD_BLOCKED_OPACITY
        : AD_NORMAL_OPACITY;

    for (const adTile of adLayerTiles) {
        adTile.opacity = targetOpacity;
    }
}

function updateClueLayerVisibility() {
    const sniffLoaded = isLoaded("action_sniff");
    const targetOpacity = sniffLoaded
        ? CLUE_VISIBLE_OPACITY
        : CLUE_HIDDEN_OPACITY;

    if (sniffLoaded === lastSniffLoaded) {
        let settled = true;
        for (const clueTile of clueLayerTiles) {
            if (Math.abs(clueTile.opacity - targetOpacity) > 0.01) {
                settled = false;
                break;
            }
        }
        if (settled) return;
    }
    lastSniffLoaded = sniffLoaded;

    for (const clueTile of clueLayerTiles) {
        clueTile.opacity = Math.abs(clueTile.opacity - targetOpacity) < 0.01
            ? targetOpacity
            : lerp(clueTile.opacity, targetOpacity, 0.35);
    }
}

function createInteractableHighlights() {
    for (const interactable of INTERACTABLES) {
        for (const cell of interactable.cells) {
            add([
                rect(TILE_W + 2, TILE_H + 2),
                pos(cell.x * TILE_W - 1, cell.y * TILE_H - 1),
                anchor("topleft"),
                color(160, 230, 255),
                outline(2, rgb(180, 255, 255)),
                opacity(0.18),
                blend("screen"),
                z(95),
                "interactive_highlight",
                { tileX: cell.x, tileY: cell.y, baseOpacity: 0.18 },
            ]);
        }
    }
}

function updateInteractableHighlights() {
    const highlights = get("interactive_highlight");
    if (!highlights || highlights.length === 0) return;
    const t = time();
    for (const highlight of highlights) {
        highlight.opacity = highlight.baseOpacity + 0.08 + Math.sin(t * 6 + highlight.tileX * 0.8 + highlight.tileY * 1.3) * 0.1;
    }
}

function updateInteractiveProximity() {
    const player = get("player")[0];
    if (!player || isTextboxOpen()) return;
    const playerPos = player.pos;

    for (const interactable of INTERACTABLES) {
        if (interactable.triggered) continue;
        const isClose = interactable.cells.some((cell) => {
            const cellCenter = vec2(cell.x * TILE_W + TILE_W / 2, cell.y * TILE_H + TILE_H / 2);
            return playerPos.dist(cellCenter) <= TILE_W;
        });
        if (!isClose) continue;

        let suppressDefaultText = false;
        if (typeof interactable.onTrigger === "function") {
            suppressDefaultText = interactable.onTrigger() === true;
        }

        if (!suppressDefaultText) {
            queueTextbox({
                tag: `OBJ_SCAN // ${interactable.id.toUpperCase()}`,
                channel: "CH.01",
                text: interactable.text,
                variant: "system",
            });
        }

        interactable.triggered = true;
        break;
    }
}

function updateTunnelVisibility() {
    const player = get("player")[0];
    if (!player || tunnelLayerTiles.length === 0) return;

    const playerCellX = Math.floor(player.pos.x / TILE_W);
    const playerCellY = Math.floor(player.pos.y / TILE_H);
    const activeGroupId = getNearbyTunnelGroupId(playerCellX, playerCellY);
    if (activeGroupId === lastTunnelGroupId) {
        let settled = true;
        for (const tunnelTile of tunnelLayerTiles) {
            const targetOpacity = activeGroupId != null && tunnelTile.tunnelGroupId === activeGroupId
                ? TUNNEL_VISIBLE_OPACITY
                : TUNNEL_HIDDEN_OPACITY;
            if (Math.abs(tunnelTile.opacity - targetOpacity) > 0.01) {
                settled = false;
                break;
            }
        }
        if (settled) return;
    }
    lastTunnelGroupId = activeGroupId;

    for (const tunnelTile of tunnelLayerTiles) {
        const targetOpacity = activeGroupId != null && tunnelTile.tunnelGroupId === activeGroupId
            ? TUNNEL_VISIBLE_OPACITY
            : TUNNEL_HIDDEN_OPACITY;

        tunnelTile.opacity = Math.abs(tunnelTile.opacity - targetOpacity) < 0.01
            ? targetOpacity
            : lerp(tunnelTile.opacity, targetOpacity, 0.35);
    }
}

function updateDoorOpacity() {
    const player = get("player")[0];

    for (const door of doorOverlayTiles) {
        const isPlayerUnderDoor = player
            && player.pos.x >= door.tileX * TILE_W
            && player.pos.x < (door.tileX + 1) * TILE_W
            && player.pos.y >= door.tileY * TILE_H
            && player.pos.y < (door.tileY + 1) * TILE_H;

        const targetOpacity = isPlayerUnderDoor
            ? DOOR_PLAYER_UNDER_OPACITY
            : DOOR_NORMAL_OPACITY;

        door.opacity = lerp(door.opacity, targetOpacity, 0.25);
    }
}

function isPassableCell(rows, x, y) {
    if (y < 0 || y >= rows.length) return false;
    if (x < 0 || x >= rows[y].length) return false;
    return rows[y][x] === ".";
}

function isBoundaryWallCell(rows, x, y) {
    if (rows[y][x] !== "#") return false;
    return (
        isPassableCell(rows, x + 1, y) ||
        isPassableCell(rows, x - 1, y) ||
        isPassableCell(rows, x, y + 1) ||
        isPassableCell(rows, x, y - 1)
    );
}

function addCollisionWalls(rows) {
    for (let y = 0; y < rows.length; y++) {
        let x = 0;
        while (x < rows[y].length) {
            if (!isBoundaryWallCell(rows, x, y)) {
                x++;
                continue;
            }

            const runStart = x;
            while (x < rows[y].length && isBoundaryWallCell(rows, x, y)) {
                x++;
            }
            const runLength = x - runStart;

            add([
                rect(runLength * TILE_W, TILE_H),
                pos(runStart * TILE_W, y * TILE_H),
                opacity(0),
                area(),
                body({ isStatic: true }),
                "wall",
            ]);
        }
    }
}

/**
 * Zero-GameObj path grid for robot/human getPath.
 *
 * KAPLAY addLevel used to spawn a tile object per map cell (and even
 * `".": () => []` still allocated GameObjs) — ~7.6k objects on 126×61,
 * which dominated the ~10k entity count and tanked FPS. Nav only needs
 * the walkable mask from collision rows.
 *
 * API matches level.getPath(from, to, { allowDiagonals }) return shape:
 * [startWorld, ...tileCenters, endWorld] or null.
 */
function createPathLevel(rows) {
    const height = rows.length;
    const width = rows[0]?.length ?? 0;
    const blocked = new Uint8Array(width * height);
    let obstacleTiles = 0;

    for (let y = 0; y < height; y++) {
        const row = rows[y];
        for (let x = 0; x < width; x++) {
            if (row[x] === "#") {
                blocked[y * width + x] = 1;
                obstacleTiles += 1;
            }
        }
    }
    setPerfStat("pathObstacles", obstacleTiles);
    setPerfStat("pathCells", width * height);

    const keyOf = (x, y) => y * width + x;

    function inBounds(x, y) {
        return x >= 0 && y >= 0 && x < width && y < height;
    }

    function isBlocked(x, y) {
        return !inBounds(x, y) || blocked[keyOf(x, y)] === 1;
    }

    function pushOpen(open, node) {
        open.push(node);
        let i = open.length - 1;
        while (i > 0) {
            const p = (i - 1) >> 1;
            if (open[p].f <= open[i].f) break;
            const tmp = open[p];
            open[p] = open[i];
            open[i] = tmp;
            i = p;
        }
    }

    function popOpen(open) {
        const top = open[0];
        const last = open.pop();
        if (open.length === 0 || last === undefined) return top;
        open[0] = last;
        let i = 0;
        for (;;) {
            const l = i * 2 + 1;
            const r = l + 1;
            let smallest = i;
            if (l < open.length && open[l].f < open[smallest].f) smallest = l;
            if (r < open.length && open[r].f < open[smallest].f) smallest = r;
            if (smallest === i) break;
            const tmp = open[i];
            open[i] = open[smallest];
            open[smallest] = tmp;
            i = smallest;
        }
        return top;
    }

    function neighbors(x, y, allowDiagonals) {
        const out = [];
        const orth = [
            [-1, 0],
            [1, 0],
            [0, -1],
            [0, 1],
        ];
        for (const [dx, dy] of orth) {
            const nx = x + dx;
            const ny = y + dy;
            if (!isBlocked(nx, ny)) out.push(nx, ny);
        }
        if (allowDiagonals) {
            const diags = [
                [-1, -1],
                [1, -1],
                [-1, 1],
                [1, 1],
            ];
            for (const [dx, dy] of diags) {
                const nx = x + dx;
                const ny = y + dy;
                if (isBlocked(nx, ny)) continue;
                // No corner-cutting through walls.
                if (isBlocked(x + dx, y) || isBlocked(x, y + dy)) continue;
                out.push(nx, ny);
            }
        }
        return out;
    }

    function getTilePath(fromTile, toTile, opts = {}) {
        const sx = Math.floor(fromTile.x);
        const sy = Math.floor(fromTile.y);
        const tx = Math.floor(toTile.x);
        const ty = Math.floor(toTile.y);

        if (!inBounds(sx, sy) || !inBounds(tx, ty)) return null;
        if (isBlocked(tx, ty)) return null;
        if (sx === tx && sy === ty) return [];

        const allowDiagonals = opts.allowDiagonals === true;
        const startKey = keyOf(sx, sy);
        const goalKey = keyOf(tx, ty);
        const open = [];
        const cameFrom = new Int32Array(width * height).fill(-1);
        const gScore = new Float64Array(width * height).fill(Infinity);
        const closed = new Uint8Array(width * height);

        gScore[startKey] = 0;
        pushOpen(open, {
            key: startKey,
            x: sx,
            y: sy,
            f: Math.hypot(tx - sx, ty - sy),
        });

        while (open.length > 0) {
            const current = popOpen(open);
            if (closed[current.key]) continue;
            closed[current.key] = 1;

            if (current.key === goalKey) {
                const tiles = [];
                let k = goalKey;
                while (k !== startKey) {
                    const x = k % width;
                    const y = (k / width) | 0;
                    tiles.push(vec2(x, y));
                    k = cameFrom[k];
                    if (k < 0) return null;
                }
                tiles.push(vec2(sx, sy));
                tiles.reverse();
                return tiles;
            }

            const n = neighbors(current.x, current.y, allowDiagonals);
            for (let i = 0; i < n.length; i += 2) {
                const nx = n[i];
                const ny = n[i + 1];
                const nk = keyOf(nx, ny);
                if (closed[nk]) continue;

                const step = nx !== current.x && ny !== current.y ? Math.SQRT2 : 1;
                const tentative = gScore[current.key] + step;
                if (tentative >= gScore[nk]) continue;

                cameFrom[nk] = current.key;
                gScore[nk] = tentative;
                pushOpen(open, {
                    key: nk,
                    x: nx,
                    y: ny,
                    f: tentative + Math.hypot(tx - nx, ty - ny),
                });
            }
        }

        return null;
    }

    function getPath(fromWorld, toWorld, opts = {}) {
        if (!fromWorld || !toWorld) return null;
        const fromTile = vec2(
            Math.floor(fromWorld.x / TILE_W),
            Math.floor(fromWorld.y / TILE_H),
        );
        const toTile = vec2(
            Math.floor(toWorld.x / TILE_W),
            Math.floor(toWorld.y / TILE_H),
        );
        const tiles = getTilePath(fromTile, toTile, opts);
        if (!tiles) return null;

        const mids = tiles.slice(1, -1).map((t) =>
            vec2(t.x * TILE_W + TILE_W / 2, t.y * TILE_H + TILE_H / 2),
        );
        return [fromWorld, ...mids, toWorld];
    }

    return {
        getPath,
        getTilePath,
        numColumns: () => width,
        numRows: () => height,
        tileWidth: () => TILE_W,
        tileHeight: () => TILE_H,
    };
}

function addMapLights(map) {
    const lightLayer = getLayer(map, LIGHT_LAYER_NAMES);
    if (!lightLayer) return;

    const placed = [];
    for (let y = 0; y < lightLayer.height; y++) {
        for (let x = 0; x < lightLayer.width; x++) {
            const gid = getTileGid(lightLayer, x, y);
            if (gid === 0) continue;

            const posVec = vec2(x * TILE_W + TILE_W / 2, y * TILE_H + TILE_H / 2);
            const room = getRoomAt(posVec) || getNearestRoom(posVec);
            const style = getLightStyleForGid(gid);

            setLight(`map_light_${x}_${y}_${gid}`, {
                pos: posVec,
                radius: style.radius,
                color: style.color,
                intensity: style.intensity,
                flicker: style.flicker,
                pulse: style.pulse,
                roomId: room ? room.id : null,
                gid,
                alarm: style.alarm,
                alarmIntensity: style.alarmIntensity,
                baseIntensity: style.intensity,
            });
            placed.push(`(${x},${y}) gid=${gid}`);
        }
    }

    console.info(`[lighting] Light-layer emitters (${placed.length}):`, placed.join(" | "));
}

function addClueLights(map) {
    const clueLayer = getLayer(map, CLUE_LAYER_NAMES);
    if (!clueLayer) return;

    const placed = [];
    for (let y = 0; y < clueLayer.height; y++) {
        for (let x = 0; x < clueLayer.width; x++) {
            const gid = getTileGid(clueLayer, x, y);
            if (gid === 0) continue;

            const posVec = vec2(x * TILE_W + TILE_W / 2, y * TILE_H + TILE_H / 2);
            const room = getRoomAt(posVec) || getNearestRoom(posVec);

            setLight(`clue_light_${x}_${y}_${gid}`, {
                pos: posVec,
                radius: 76,
                color: [255, 255, 255],
                intensity: 0.52,
                pulse: true,
                flicker: false,
                roomId: room ? room.id : null,
                clue: true,
            });
            placed.push(`(${x},${y}) gid=${gid}`);
        }
    }

    console.info(`[lighting] Clue-layer emitters (${placed.length}):`, placed.join(" | "));
}

function spawnEntities(map) {
    const humanPos = worldCellByAnchorOffset(map, TILE_POSITIONS.humanGuard);
    spawnHomelessGuy(worldCellByAnchorOffset(map, TILE_POSITIONS.homelessGuy), {
        hasLineOfSight: hasLineOfSightWorld,
        sightRadius: 90,
    });

    spawnHuman({
        pos: humanPos,
        facingAngle: 180,
        ...r2SpawnProximityAlarmOptions(humanPos),
        patrol: {
            loop: true,
            speed: 40,
            turnSpeed: 220,
            steps: [
                { x: humanPos.x, y: humanPos.y, face: 180, wait: 1.5 },
                { x: humanPos.x + 48, y: humanPos.y, face: 0, wait: 1.0, rotate: "cw" },
                { x: humanPos.x + 48, y: humanPos.y + 32, face: 90, wait: 1.0 },
                { x: humanPos.x, y: humanPos.y + 32, face: 180, wait: 0.8 },
            ],
        },
    });
    for (const humanOffset of TILE_POSITIONS.patrolHumans) {
        const patrolPos = worldCellByAnchorOffset(map, humanOffset);
        spawnHuman({
            pos: patrolPos,
            facingAngle: 0,
            ...r2SpawnProximityAlarmOptions(patrolPos),
            patrol: {
                loop: true,
                speed: 36,
                turnSpeed: 220,
                steps: [
                    { x: patrolPos.x, y: patrolPos.y, face: 0, wait: 0.8 },
                    { x: patrolPos.x + 32, y: patrolPos.y, face: 0, wait: 0.8 },
                    { x: patrolPos.x + 32, y: patrolPos.y + 16, face: 90, wait: 0.6 },
                    { x: patrolPos.x, y: patrolPos.y + 16, face: 180, wait: 0.8 },
                ],
            },
        });
    }
    for (const humanOffset of TILE_POSITIONS.circlingHumans) {
        const patrolPos = worldCellByAnchorOffset(map, humanOffset);
        spawnHuman({
            pos: patrolPos,
            facingAngle: 0,
            ...r2SpawnProximityAlarmOptions(patrolPos),
            patrol: {
                loop: true,
                speed: 36,
                turnSpeed: 220,
                steps: [
                    { x: patrolPos.x, y: patrolPos.y - 32, face: 270, wait: 0.35 },
                    { x: patrolPos.x + 32, y: patrolPos.y, face: 0, wait: 0.35 },
                    { x: patrolPos.x, y: patrolPos.y + 32, face: 90, wait: 0.35 },
                    { x: patrolPos.x - 32, y: patrolPos.y, face: 180, wait: 0.35 },
                ],
            },
        });
    }
    for (const humanOffset of TILE_POSITIONS.suiteHumans) {
        const suitePos = worldCellByAnchorOffset(map, humanOffset);
        spawnHuman({
            pos: suitePos,
            facingAngle: 180,
            spriteSet: "suite",
            visionConeAngle: 360,
            ...r2SpawnProximityAlarmOptions(suitePos),
            ramDropType: "upgrade",
            ramUnlockModuleIds: ["action_sniff", "utility_ad_blocker"],
        });
    }
    for (const robotOffset of TILE_POSITIONS.guardRobots) {
        spawnGuardRobot(worldCellByAnchorOffset(map, robotOffset), false);
    }
}

export function createLevel() {
    const map = getSpriteFusionMap();

    clearLights();
    clearScentMarks();
    clearRooms();
    clearAlarmLockdowns();
    clearCutsceneLocks();
    freeBakedMapLayers();
    currentLevel = null;

    collisionRows = buildCollisionRows(map);
    sightBlockRows = buildSightBlockRows(map);

    if (!hasWalkableLayer(map)) {
        console.warn("SpriteFusion map has no `walkable` layer; no map tiles will be passable.");
    }

    const regionRooms = buildRoomRectsFromRegionLayer(map);
    const roomDefs = regionRooms && regionRooms.length > 0
        ? regionRooms
        : buildRoomRectsFromCollision(collisionRows);
    setRooms(roomDefs);

    renderMapTiles(map);
    refreshLayerTileCaches();
    updateAdLayerVisibility();
    updateClueLayerVisibility();
    updateTunnelVisibility();
    currentLevel = createPathLevel(collisionRows);
    addCollisionWalls(collisionRows);
    addMapLights(map);
    addClueLights(map);

    createInteractableHighlights();
    createRamLatchPuzzle(map);

    for (const checkpointOffset of TILE_POSITIONS.checkpoints) {
        const posVec = worldCellByAnchorOffset(map, checkpointOffset);
        const room = getRoomAt(posVec);
        addCheckpointAt(posVec, room ? room.id : 1);
    }

    const markerCheckpoints = getMarkerCells(map, ["checkpoints", "checkpoint", "spawn"]);
    if (markerCheckpoints.length > 0) {
        for (const cell of markerCheckpoints) {
            const posVec = cellToWorldCenter(cell);
            const room = getRoomAt(posVec);
            addCheckpointAt(posVec, room ? room.id : 1);
        }
    } else {
        const spawn = getSpawnPos();
        const spawnRoom = getRoomAt(spawn);
        addCheckpointAt(spawn, spawnRoom ? spawnRoom.id : 1);
    }

    spawnEntities(map);
    onUpdate(updateDoorOpacity);
    onUpdate(updateAdLayerVisibility);
    onUpdate(updateClueLayerVisibility);
    onUpdate(updateTunnelVisibility);
    onUpdate(updateInteractableHighlights);
    onUpdate(updateInteractiveProximity);

    return currentLevel;
}
